import { useState, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { MagnifyingGlassIcon, XMarkIcon, EyeIcon, LinkIcon } from "@heroicons/react/20/solid";
import { Badge } from "src/components/base/badge";
import type { View } from "./types";
import { VIEW_LABELS, VIEW_COLORS } from "./types";
import type { UnifiedEvent } from "./data/events";
import { UNIFIED_EVENTS } from "./data/events";

export function SearchBar({
    onNavigate,
    onInvestigate,
}: {
    onNavigate: (v: View) => void;
    onInvestigate: (event: UnifiedEvent) => void;
}) {
    const { t } = useTranslation();
    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    const results = useMemo(() => {
        if (query.length < 2) return [];
        const q = query.toLowerCase();
        return UNIFIED_EVENTS.filter(
            (e) =>
                e.searchableText.toLowerCase().includes(q) ||
                e.id.toLowerCase().includes(q) ||
                e.title.toLowerCase().includes(q),
        );
    }, [query]);

    useEffect(() => {
        function handleClick(e: MouseEvent) {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        }
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, []);

    return (
        <div ref={ref} className="relative">
            <div className="flex items-center gap-2 rounded-xl border border-zinc-200/40 bg-white/60 px-3.5 py-2 shadow-sm backdrop-blur-xl transition-all duration-200 focus-within:border-blue-400/40 focus-within:shadow-md focus-within:shadow-blue-500/10 dark:border-zinc-700/40 dark:bg-zinc-800/60">
                <MagnifyingGlassIcon className="h-4 w-4 text-zinc-400" />
                <input
                    type="text"
                    value={query}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setOpen(true);
                    }}
                    onFocus={() => setOpen(true)}
                    placeholder={t("search.placeholder")}
                    className="w-48 bg-transparent text-sm text-zinc-900 outline-none placeholder:text-zinc-400 sm:w-72 dark:text-white"
                />
                {query && (
                    <button
                        onClick={() => {
                            setQuery("");
                            setOpen(false);
                        }}
                    >
                        <XMarkIcon className="h-4 w-4 text-zinc-400 hover:text-zinc-600" />
                    </button>
                )}
            </div>
            {open && query.length >= 2 && (
                <div className="absolute right-0 top-full z-50 mt-2 max-h-96 w-[32rem] overflow-y-auto rounded-2xl border border-zinc-200/40 bg-white/80 shadow-2xl shadow-blue-500/10 backdrop-blur-2xl dark:border-zinc-700/40 dark:bg-zinc-800/80">
                    {results.length === 0 ? (
                        <p className="p-6 text-center text-sm text-zinc-400">{t("search.noResults")}</p>
                    ) : (
                        results.map((event) => (
                            <div
                                key={event.id}
                                className="flex items-center gap-3 border-b border-zinc-100/60 px-4 py-3 transition-all duration-200 last:border-0 hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-transparent dark:border-zinc-700/30 dark:hover:from-blue-950/30 dark:hover:to-transparent"
                            >
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                        <Badge color={VIEW_COLORS[event.system]}>{VIEW_LABELS[event.system]}</Badge>
                                        <span className="font-mono text-xs text-zinc-400">{event.id}</span>
                                        <span className="font-mono text-xs text-zinc-400">{event.time}</span>
                                    </div>
                                    <p className="mt-1 text-sm font-medium text-zinc-700 dark:text-zinc-300">
                                        {event.title}
                                    </p>
                                    <div className="mt-1 flex flex-wrap gap-1">
                                        {event.correlated.map((sys) => (
                                            <button
                                                key={sys}
                                                onClick={() => {
                                                    onNavigate(sys);
                                                    setOpen(false);
                                                }}
                                            >
                                                <Badge color={VIEW_COLORS[sys]}>
                                                    <LinkIcon className="mr-0.5 inline h-2.5 w-2.5" />
                                                    {VIEW_LABELS[sys]}
                                                </Badge>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <button
                                    onClick={() => {
                                        onInvestigate(event);
                                        setOpen(false);
                                    }}
                                    className="shrink-0 rounded-lg p-2 text-zinc-400 transition-colors hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-blue-950 dark:hover:text-blue-400"
                                    title="Investigate"
                                >
                                    <EyeIcon className="h-4 w-4" />
                                </button>
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}
